import { AppBar, CircularProgress, Grid, Tab, Tabs } from "@mui/material";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { TabPanel } from "components/TabPanel";
import TabToolbarPlaceholder from "components/TabToolbarPlaceholder";
import { RefreshJobAlert } from "components/job/RefreshJobAlert";
import { RequestedPartsTab } from "components/job/parts/RequestedPartsTab";
import { isEmpty, toDateString } from "helpers";
import { cloneDeep } from "lodash";
import { meterReadingsNotRequiredOrFilled } from "operations/mutations/UpdateVisit";
import { ChecklistTab } from "pages/jobs/jobDetails/tabs/ChecklistTab";
import { CompleteVisitButton } from "pages/jobs/jobDetails/tabs/CompleteVisitButton";
import { ExtrasTab } from "pages/jobs/jobDetails/tabs/ExtrasTab";
import { FileTab } from "pages/jobs/jobDetails/tabs/FileTab";
import { JobDetailTab } from "pages/jobs/jobDetails/tabs/JobDetailTab";
import { MeterTab } from "pages/jobs/jobDetails/tabs/MeterTab";
import { NotesTab } from "pages/jobs/jobDetails/tabs/NotesTab";
import { PartsTab } from "pages/jobs/jobDetails/tabs/PartsTab";
import { TimesTab } from "pages/jobs/jobDetails/tabs/TimesTab";
import { VisitTab } from "pages/jobs/jobDetails/tabs/VisitTab";
import { SignoffDialog } from "pages/jobs/jobDetails/tabs/completion/SignoffDialog";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import {
  resetRefreshNotifications,
  selectChecklistsIncomplete,
  selectSelectedJobVisit,
  selectTravelTabInvalid,
  setJobTab,
  setVisitValue,
  validateVisit,
} from "store/slices/jobs.store";
import { setAppBar } from "store/slices/menu.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

const errorKeys = {
  visit: ["solutionDescription", "actionId1", "actionId2", "actionId3"],
  times: ["startDate", "startTime", "stopDate", "stopTime", "work", "travel"],
  extras: [],
  checklists: [],
  parts: [],
  files: [],
  notes: [],
  meters: [],
};

export const JobDetailsPage: FC = () => {
  const tabValue = useAppSelector((s) => s.jobs.jobTab);
  const job = useAppSelector(selectPageSelectedJob);
  const { refreshJobNotifications } = useAppSelector((s) => s.jobs);
  const {
    errors,
    meterReadings,
    autoEndTime,
    workTimes,
    travelTimes,
    metersNotSaved,
    checklists,
    submitLoading,
  } = useAppSelector(selectSelectedJobVisit);
  const { files, visits } = job;
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const extrasEnabled = useAppSelector((s) => isFlagEnabled(s, "Extras"));
  const travelTabInvalid = useAppSelector(selectTravelTabInvalid);
  const checklistsIncomplete = useAppSelector(selectChecklistsIncomplete);
  const [fab, setFab] = useState<JSX.Element>();
  const [openSignoff, setOpenSignoff] = useState(false);

  const invalidTravelTime =
    !isEmpty(travelTimes) &&
    travelTimes[0].startTime !== null &&
    travelTimes[travelTimes.length - 1].stopTime === null;

  const hasChecklistPrestart = useMemo(() => {
    return (
      !!job.checklists?.length &&
      job.checklists.some((checklist) => checklist.questions.some((q) => q.isRequiredPreStart))
    );
  }, [job.checklists]);

  const tabHasError = useCallback(
    (tabName: keyof typeof errorKeys) => {
      let searchKeys = errorKeys[tabName];
      let currentErrorKeys = Object.keys(errors);
      for (let key of currentErrorKeys) {
        if (searchKeys.some((sub) => key.includes(sub))) return true;
      }
      return false;
    },
    [errors]
  );

  const jobHasIncompletePreWorkChecklists =
    !isEmpty(checklists) &&
    checklists.some(
      (c) =>
        c.hasPreStart &&
        c.checklist.questions
          .filter((x) => x.isRequiredPreStart)
          .some((q) => q.answer === null || q.answer === "0")
    );

  useEffect(() => {
    dispatch(
      setAppBar({
        title: job?.externalId ?? "",
        goingBack: true,
      })
    );
  }, [dispatch, job]);

  const justOnInitialMount = useRef(false); // skip use effect on mount
  useEffect(() => {
    if (!justOnInitialMount.current) {
      dispatch(validateVisit());
      justOnInitialMount.current = true;
    }
  }, [dispatch]);

  const onCompleteVisit = useCallback(() => {
    if (invalidTravelTime) {
      dispatch(addSnackbarMessage({ key: "No-end-travel" }));
    } else {
      if (meterReadings && meterReadingsNotRequiredOrFilled(meterReadings, job)) {
        if (autoEndTime) {
          let workTimesCopy = cloneDeep(workTimes);
          let last = workTimesCopy.pop();
          if (last) {
            last.stopDate = toDateString(Date.now());
            last.stopTime = toDateString(Date.now());
            dispatch(setVisitValue({ key: "workTimes", value: [...workTimesCopy, last] }));
          }
        }
        setOpenSignoff(true);
        dispatch(setJobTab({ tab: "visit" }));
      } else {
        dispatch(addSnackbarMessage({ key: "CompleteVisitWithParts-fail-meters" }));
      }
      return null;
    }
  }, [autoEndTime, dispatch, invalidTravelTime, job, meterReadings, workTimes]);

  const onDimissNotification = useCallback(
    (key: string, tab: string | null) => {
      if (tab) dispatch(setJobTab({ tab }));
      dispatch(resetRefreshNotifications({ key }));
    },
    [dispatch]
  );

  return (
    <>
      <BackdropPrimaryMain open={submitLoading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar
        position="fixed"
        sx={{
          mt: 7,
          backgroundColor: `primary.main`,
        }}
      >
        <Tabs
          scrollButtons
          value={tabValue}
          onChange={(_, newValue: string) => {
            dispatch(setJobTab({ tab: newValue }));
          }}
          variant="scrollable"
          textColor="inherit"
        >
          <Tab label={intl.formatMessage({ id: "general.details" })} value="details" />
          {!isEmpty(visits) && (
            <Tab label={intl.formatMessage({ id: "general.visits" })} value="visits" />
          )}
          {!isEmpty(job.preOrderedParts) && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.parts" })}
                  {tabHasError("parts") ? "*" : null}
                </>
              }
              value="parts"
              data-testid="partsTab"
            />
          )}
          {hasChecklistPrestart && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.checklists" })}
                  {tabHasError("checklists") || checklistsIncomplete ? "*" : null}
                </>
              }
              value="checklists"
              data-testid="checklistsTab"
            />
          )}
          {!isEmpty(files) && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "general.files" })}
                  {tabHasError("files") ? "*" : null}
                </>
              }
              value="files"
              data-testid="filesTab"
            />
          )}
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "job.times" })}
                {tabHasError("times") || travelTabInvalid ? "*" : null}
              </>
            }
            value="times"
            data-testid="timesTab"
          />
          {extrasEnabled && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.extras" })}
                  {tabHasError("extras") ? "*" : null}
                </>
              }
              value="extras"
              data-testid="extrasTab"
              disabled={jobHasIncompletePreWorkChecklists}
            />
          )}
          {isEmpty(job.preOrderedParts) && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.parts" })}
                  {tabHasError("parts") ? "*" : null}
                </>
              }
              value="parts"
              data-testid="partsTab"
              disabled={jobHasIncompletePreWorkChecklists}
            />
          )}
          {!hasChecklistPrestart && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.checklists" })}
                  {tabHasError("checklists") || checklistsIncomplete ? "*" : null}
                </>
              }
              value="checklists"
              data-testid="checklistsTab"
            />
          )}
          {isEmpty(files) && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "general.files" })}
                  {tabHasError("files") ? "*" : null}
                </>
              }
              value="files"
              data-testid="filesTab"
              disabled={jobHasIncompletePreWorkChecklists}
            />
          )}
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "job.notes" })}
                {tabHasError("notes") ? "*" : null}
              </>
            }
            value="notes"
            data-testid="notesTab"
            disabled={jobHasIncompletePreWorkChecklists}
          />
          {job.meters && !!job.meters?.length && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.meters" })}
                  {tabHasError("meters") || metersNotSaved ? "*" : null}
                </>
              }
              value="meters"
              data-testid="metersTab"
              disabled={jobHasIncompletePreWorkChecklists}
            />
          )}
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "general.visit" })}
                {tabHasError("visit") ? "*" : null}
              </>
            }
            className="e2e-visit-tab"
            value="visit"
            data-testid="visitTab"
            disabled={jobHasIncompletePreWorkChecklists}
          />
        </Tabs>
      </AppBar>
      <TabToolbarPlaceholder />

      <RefreshJobAlert
        dismissNotification={onDimissNotification}
        refreshJobNotifications={refreshJobNotifications}
      />

      <TabPanel index="details" value={tabValue}>
        <JobDetailTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="requestedParts" value={tabValue}>
        <RequestedPartsTab parts={job.preOrderedParts} />
      </TabPanel>
      <TabPanel index="times" value={tabValue}>
        <TimesTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="extras" value={tabValue}>
        <ExtrasTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="parts" value={tabValue}>
        <PartsTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="files" value={tabValue}>
        <FileTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="notes" value={tabValue}>
        <NotesTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="meters" value={tabValue}>
        <MeterTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="checklists" value={tabValue}>
        <ChecklistTab addButton setFab={setFab} />
      </TabPanel>
      <TabPanel index="visit" value={tabValue}>
        <VisitTab handleFinished={() => navigate(-1)} setFab={setFab} />
      </TabPanel>
      <Grid
        container
        direction="column"
        width="inherit"
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
        }}
      >
        <Grid item>{fab}</Grid>
        <Grid item>
          <CompleteVisitButton onCompleteVisit={onCompleteVisit} />
        </Grid>
      </Grid>
      <SignoffDialog open={openSignoff} handleClose={() => setOpenSignoff(false)} />
    </>
  );
};
